import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Play } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  fetchPrzebiegi,
  fetchTabeleDoUzgodnienia,
  podgladPrzebiegu,
  przerwijPrzebieg,
  wznowPrzebieg,
  zacznijPrzebieg,
  type Przebieg,
} from '@/lib/uzgadnianie-api';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/lib/i18n';
import { useToast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  PRZEBIEG UZGADNIAJĄCY — JEDNA TABELA, PACZKAMI, Z POTWIERDZENIEM PO KAŻDEJ.
 *
 *  ⚠️⚠️ ZAMÓWIONE 21.09.2026: „Musi to iść paczkami i dopiero po zatwierdzeniu przez każdy węzeł
 *  paczki leci kolejna paczka — inaczej znów się zajebiemy w akcji". Poprzednie narzędzie (pełny
 *  odczyt wszystkich tabel naraz) wygenerowało 3430 operacji i 2,2 mln zmian, położyło chmurowego
 *  mastera i unieruchomiło wyspę na trzy dni.
 *
 *  ⚠️⚠️ EKRAN MA MÓWIĆ, GDZIE PRZEBIEG STOI I NA KIM — nie malować paska postępu. „Ile zostało"
 *  nie istnieje i nie będzie udawane: portal nie wie, ile wierszy ma węzeł, dopóki go nie przejdzie.
 *  Zgadnięty termin przy robocie trwającej godzinami jest gorszy niż jego brak.
 *
 *  ⚠️ PRZEBIEG STOI POD „STANEM TABEL", bo kolejność na ekranie ma wymuszać kolejność myślenia:
 *  najpierw zobacz, KTÓRA tabela się rozjechała, dopiero potem ją uzgadniaj.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function PrzebiegUzgadniajacy({
  groupId,
  wybor = null,
}: {
  groupId: string;
  /**
   * TABELA WSKAZANA Z „STANU TABEL" („Uzgodnij”). Nowy obiekt przy każdym kliknięciu, żeby ta sama
   * tabela wybrana drugi raz też zadziałała. ⚠️ Tylko USTAWIA wybór — „Zacznij" dalej klika człowiek,
   * bo przebieg kolejkuje zapisy do baz klienta przez wiele godzin.
   */
  wybor?: { sps: string } | null;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const [tabela, setTabela] = useState('');
  const [vmid, setVmid] = useState('');
  const [pracuje, setPracuje] = useState(false);

  useEffect(() => {
    if (wybor === null) return;
    setTabela(wybor.sps);
    setVmid('');
  }, [wybor]);

  const slownik = useQuery({
    queryKey: ['uzgadnianie-tabele', groupId],
    queryFn: () => fetchTabeleDoUzgodnienia(groupId),
    enabled: groupId !== '',
  });

  const wybrana = slownik.data?.tabele.find((x) => x.sps === tabela) ?? null;
  const automaty = slownik.data?.automaty ?? [];
  /** ⚠️ Tabela per automat bez numeru jest odmawiana przez portal — nie dajmy jej nawet kliknąć. */
  const gotowyDoStartu = wybrana !== null && (wybrana.wyspowa || vmid !== '');

  /**
   * ⚠️⚠️ CO SIĘ STANIE PO KLIKNIĘCIU — zdanie z portalu, nie z ekranu. Przycisk nazywa się
   * „Zacznij", a kolejkuje zapisy do baz produkcyjnych klienta przez wiele godzin; zdanie sklejone
   * tutaj z kilku liczb rozjechałoby się z tym, co przebieg naprawdę zrobi.
   */
  const podglad = useQuery({
    queryKey: ['uzgadnianie-podglad', groupId, tabela, vmid],
    queryFn: () => podgladPrzebiegu(groupId, tabela, vmid === '' ? null : Number(vmid)),
    enabled: gotowyDoStartu,
  });

  const przebiegi = useQuery({
    queryKey: ['uzgadnianie', groupId],
    queryFn: () => fetchPrzebiegi(groupId),
    enabled: groupId !== '',
    /**
     * ⚠️ DOPYTUJEMY CZĘŚCIEJ, GDY COŚ IDZIE. Krok zapada co minutę po stronie portalu, więc ekran
     * odświeżany raz na minutę pokazywałby stan sprzed kroku i wyglądałby na zawieszony.
     */
    refetchInterval: (q) => (q.state.data?.some((p) => p.stan === 'TRWA') === true ? 10_000 : 60_000),
  });

  const lista = przebiegi.data ?? [];
  const biezacy = lista.find((p) => p.stan === 'TRWA' || p.stan === 'WSTRZYMANY') ?? null;
  const historia = lista.filter((p) => p !== biezacy).slice(0, 4);

  const zacznij = async (): Promise<void> => {
    if (wybrana === null) return;
    setPracuje(true);
    try {
      const w = await zacznijPrzebieg(groupId, tabela, wybrana.wyspowa ? null : Number(vmid));
      toast.success(t('uzgadnianie.zaczeto').replace('{n}', String(w.wezly.length)));
      /** ⚠️ Węzeł pominięty musi paść z nazwy — jego brak w przebiegu czyta się jak zgoda. */
      if (w.zaStare.length > 0) {
        toast.toast(t('uzgadnianie.zaStare').replace('{wezly}', w.zaStare.join(', ')));
      }
      await qc.invalidateQueries({ queryKey: ['uzgadnianie', groupId] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setPracuje(false);
    }
  };

  const steruj = async (co: 'przerwij' | 'wznow', id: string): Promise<void> => {
    setPracuje(true);
    try {
      await (co === 'przerwij' ? przerwijPrzebieg(groupId, id) : wznowPrzebieg(groupId, id));
      await qc.invalidateQueries({ queryKey: ['uzgadnianie', groupId] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setPracuje(false);
    }
  };

  return (
    <section className="space-y-2 rounded-lg border border-border p-3">
      <h2 className="text-sm font-medium">{t('uzgadnianie.tytul')}</h2>
      <p className="text-[11px] text-muted-foreground">{t('uzgadnianie.poCo')}</p>

      {biezacy === null ? (
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="h-8 rounded border border-border bg-background px-2 text-xs"
            value={tabela}
            onChange={(e) => {
              setTabela(e.target.value);
              setVmid('');
            }}
          >
            <option value="">{t('uzgadnianie.wybierzTabele')}</option>
            {(slownik.data?.tabele ?? []).map((x) => (
              <option key={x.sps} value={x.sps}>
                {x.sps}
              </option>
            ))}
          </select>

          {wybrana !== null && !wybrana.wyspowa && (
            <select
              className="h-8 rounded border border-border bg-background px-2 text-xs"
              value={vmid}
              onChange={(e) => setVmid(e.target.value)}
            >
              <option value="">{t('uzgadnianie.wybierzAutomat')}</option>
              {automaty.map((a) => (
                <option key={a.vmid} value={String(a.vmid)}>
                  {a.nazwa} ({a.vmid})
                </option>
              ))}
            </select>
          )}

          <Button
            size="sm"
            variant="outline"
            disabled={!gotowyDoStartu || podglad.data?.mozliwe !== true || pracuje}
            onClick={() => void zacznij()}
          >
            {pracuje ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
            {t('uzgadnianie.zacznij')}
          </Button>

          {/*
            ⚠️⚠️ ZDANIE OBOK PRZYCISKU, NIE W PODPOWIEDZI — ma być przeczytane, a nie do odkrycia.
            Powód odmowy idzie tą samą drogą, bo „nie da się" bez powodu zostawia człowieka przy
            przycisku, który nie działa i nie wiadomo dlaczego.
          */}
          {podglad.data !== undefined && (
            <p className="basis-full text-[11px] text-muted-foreground">
              {podglad.data.powod ?? podglad.data.opis}
            </p>
          )}
          {(podglad.data?.zaStare.length ?? 0) > 0 && (
            <p className="basis-full text-[11px] text-[var(--warning)]">
              {t('uzgadnianie.zaStare').replace('{wezly}', (podglad.data?.zaStare ?? []).join(', '))}
            </p>
          )}
        </div>
      ) : (
        <Biezacy p={biezacy} pracuje={pracuje} onSteruj={(co) => void steruj(co, biezacy.id)} />
      )}

      {historia.length > 0 && (
        <ul className="space-y-0.5 border-t border-border pt-2">
          {historia.map((p) => (
            <li key={p.id} className="text-[11px] text-muted-foreground">
              <span className="font-mono">{opisTabeli(p)}</span> · {t(`uzgadnianie.stan.${p.stan}`)} ·{' '}
              {t('uzgadnianie.podsumowanie')
                .replace('{paczek}', String(p.paczek))
                .replace('{rozeslanych}', String(p.wierszyRozeslanych))}
              {/* ⚠️ Pominięte węzły MUSZĄ być widoczne także w historii — bez nich „zrobiony" kłamie. */}
              {p.pominieteWezly.length > 0 && (
                <span className="text-[var(--warning)]">
                  {' '}
                  · {t('uzgadnianie.pominiete').replace('{wezly}', p.pominieteWezly.join(', '))}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const opisTabeli = (p: Przebieg): string => (p.vmid === null ? p.tabela : `${p.tabela} (${p.vmid})`);

function Biezacy({
  p,
  pracuje,
  onSteruj,
}: {
  p: Przebieg;
  pracuje: boolean;
  onSteruj: (co: 'przerwij' | 'wznow') => void;
}) {
  const { t } = useI18n();

  return (
    <div className="space-y-1 rounded border border-border bg-muted/20 p-2 text-xs">
      <p className="font-medium">
        <span className="font-mono">{opisTabeli(p)}</span>{' '}
        <span className={cn(p.stan === 'WSTRZYMANY' ? 'text-[var(--warning)]' : 'text-[var(--success)]')}>
          {t(`uzgadnianie.stan.${p.stan}`)}
        </span>
      </p>

      {/*
        ⚠️⚠️ GDZIE PRZEBIEG STOI I NA KIM, a nie „ile procent". Węzeł źródłowy i numer w kolejce to
        jedyne liczby, które portal zna na pewno; wszystko poza nimi byłoby zgadywaniem.
      */}
      <p className="text-muted-foreground">
        {t('uzgadnianie.gdzie')
          .replace('{wezel}', p.wezel ?? '—')
          .replace('{nr}', String(p.wezelNr))
          .replace('{z}', String(p.wezlow))
          .replace('{od}', String(p.od))}
      </p>

      <p className="text-muted-foreground">
        {t(`uzgadnianie.faza.${p.faza}`)} ·{' '}
        {t('uzgadnianie.podsumowanie')
          .replace('{paczek}', String(p.paczek))
          .replace('{rozeslanych}', String(p.wierszyRozeslanych))}
        {p.pominietychZnanych > 0 &&
          ` · ${t('uzgadnianie.znane').replace('{n}', String(p.pominietychZnanych))}`}
      </p>

      {/*
        ⚠️⚠️ POWÓD WSTRZYMANIA W CAŁOŚCI, PROSTO Z PORTALU. Zamiana go na własny, krótszy komunikat
        po stronie ekranu odcięłaby nazwę węzła — a wstrzymanie zdarza się dokładnie wtedy, gdy nikt
        nie patrzy, i wtedy nazwa jest jedyną rzeczą, od której da się zacząć.
      */}
      {p.powodPauzy !== null && <p className="font-medium text-[var(--warning)]">{p.powodPauzy}</p>}

      {p.pominieteWezly.length > 0 && (
        <p className="text-[var(--warning)]">
          {t('uzgadnianie.pominiete').replace('{wezly}', p.pominieteWezly.join(', '))}
        </p>
      )}

      <div className="flex items-center gap-2 pt-1">
        {p.stan === 'WSTRZYMANY' && (
          <Button size="sm" variant="outline" disabled={pracuje} onClick={() => onSteruj('wznow')}>
            {t('uzgadnianie.wznow')}
          </Button>
        )}
        <Button size="sm" variant="destructive" disabled={pracuje} onClick={() => onSteruj('przerwij')}>
          {t('uzgadnianie.przerwij')}
        </Button>
      </div>
    </div>
  );
}
